/**
 * MSAL Configuration for Azure AD B2C authentication.
 */
import {
  Configuration,
  LogLevel,
  BrowserCacheLocation,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * Environment variables for MSAL configuration.
 * These should be set in .env files for different environments.
 */
const msalEnv = {
  clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
  authority: import.meta.env.VITE_AZURE_AD_AUTHORITY,
  knownAuthorities: import.meta.env.VITE_AZURE_AD_KNOWN_AUTHORITIES?.split(',') ?? [],
  redirectUri: import.meta.env.VITE_AZURE_AD_REDIRECT_URI ?? window.location.origin,
  postLogoutRedirectUri:
    import.meta.env.VITE_AZURE_AD_POST_LOGOUT_URI ?? window.location.origin,
  apiScope: import.meta.env.VITE_API_SCOPE,
};

/**
 * MSAL configuration object.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: msalEnv.clientId,
    authority: msalEnv.authority,
    knownAuthorities: msalEnv.knownAuthorities,
    redirectUri: msalEnv.redirectUri,
    postLogoutRedirectUri: msalEnv.postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: () => {},
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Login request configuration.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', msalEnv.apiScope],
};

/**
 * Token request configuration for API calls.
 */
export const tokenRequest = {
  scopes: [msalEnv.apiScope],
};

/**
 * API request configuration (alias for tokenRequest).
 */
export const apiRequest = tokenRequest;

/**
 * Silent token request configuration.
 */
export const silentRequest = {
  scopes: [msalEnv.apiScope],
  forceRefresh: false,
};

/**
 * Create and export the MSAL instance.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL, handle redirect promise, and clean up stale sessions.
 */
export const initializeMsal = async (): Promise<void> => {
  await msalInstance.initialize();

  // Handle redirect promise
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
      return;
    }
  } catch (error) {
    console.error('Error handling redirect:', error);
  }

  // Set active account if there is one
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);

    // Force-refresh the token to pick up any role changes made since last login
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: accounts[0],
        forceRefresh: true,
      });
      // Update the active account with fresh claims (includes updated roles)
      if (response.account) {
        msalInstance.setActiveAccount(response.account);
      }
    } catch {
      // Token expired or invalid — clear stale session
      console.warn('Cached token is stale, clearing session');
      msalInstance.setActiveAccount(null);
    }
  }
};

